import React, { useState, useEffect, useCallback } from 'react';
import { getRankings } from '../../api/userService';
import { useAuth } from '../../hooks/useAuth';
import Card, { CardHeader, CardContent } from '../../components/ui/Card';
import Loader from '../../components/ui/Loader';
import './Rankings.css';

// Helpers defensifs : la page ne doit JAMAIS crasher, meme si la reponse API change
const asString = (v, fallback = '') => (typeof v === 'string' ? v : fallback);
const asNumber = (v, fallback = 0) => (typeof v === 'number' && !Number.isNaN(v) ? v : fallback);
const rankKey = (rank) => {
  const r = asString(rank, 'novice').toLowerCase();
  if (r.includes('legend')) return 'rank-legendary';
  if (r.includes('grand')) return 'rank-grandmaster';
  if (r.includes('maitre') || r.includes('maître') || r.includes('master')) return 'rank-master';
  if (r.includes('champion')) return 'rank-champion';
  if (r.includes('aventurier') || r.includes('adventurer')) return 'rank-adventurer';
  return 'rank-novice';
};

const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'novice', label: 'Novice' },
  { key: 'aventurier', label: 'Aventurier' },
  { key: 'champion', label: 'Champion' },
  { key: 'maître', label: 'Maître' },
  { key: 'légendaire', label: 'Légendaire' },
];

const Rankings = () => {
  const [rankings, setRankings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();

  const loadRankings = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getRankings();
      const rows = Array.isArray(response?.rankings) ? response.rankings : [];
      setRankings(rows);
      setError(null);
    } catch (err) {
      console.error('[Rankings]', err);
      setRankings([]);
      setError('Erreur lors du chargement du classement. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);

  const currentUserId = user?.id || null;

  const filteredRankings = rankings.filter((p) => {
    if (filter === 'all') return true;
    const r = asString(p.rank).toLowerCase();
    if (filter === 'légendaire') return r.includes('legend');
    if (filter === 'maître') return r.includes('maitre') || r.includes('maître');
    return r.includes(filter);
  });

  const getRankClass = (position) => {
    if (position === 1) return 'rank-first';
    if (position === 2) return 'rank-second';
    if (position === 3) return 'rank-third';
    return '';
  };

  if (isLoading) {
    return (
      <div className="rankings-loading">
        <Loader text="Chargement du classement..." />
      </div>
    );
  }

  return (
    <div className="rankings-container">
      <h1>Classement des joueurs</h1>

      {error && <div className="rankings-error">{error}</div>}

      <div className="rankings-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`filter-button ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <h2>Top Duellistes</h2>
        </CardHeader>
        <CardContent>
          {filteredRankings.length === 0 ? (
            <div className="no-rankings">
              <p>{error ? 'Classement indisponible.' : 'Aucun joueur ne correspond à ce filtre.'}</p>
              {!error && <p className="no-rankings-hint">Jouez des parties en ligne pour apparaître ici !</p>}
            </div>
          ) : (
            <div className="rankings-table-container">
              <table className="rankings-table">
                <thead>
                  <tr>
                    <th className="position-col">Position</th>
                    <th className="player-col">Joueur</th>
                    <th className="rank-col">Rang</th>
                    <th className="wins-col">Victoires</th>
                    <th className="losses-col">Défaites</th>
                    <th className="ratio-col">Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRankings.map((player) => {
                    const position = asNumber(player.position, asNumber(player.rank, 0)) || 0;
                    const rank = asString(player.rank, 'Novice');
                    return (
                      <tr
                        key={player.id ?? position}
                        className={`${getRankClass(position)} ${player.id === currentUserId ? 'current-user' : ''}`}
                      >
                        <td className="position-col">
                          <span className="position-badge">{position}</span>
                        </td>
                        <td className="player-col">
                          <div className="player-info">
                            <div className="player-avatar player-avatar-initial">
                              {asString(player.username, '?').charAt(0).toUpperCase()}
                            </div>
                            <span className="player-name">{asString(player.username, 'Joueur')}</span>
                          </div>
                        </td>
                        <td className="rank-col">
                          <span className={`rank-badge ${rankKey(rank)}`}>{rank}</span>
                        </td>
                        <td className="wins-col">{asNumber(player.wins)}</td>
                        <td className="losses-col">{asNumber(player.losses)}</td>
                        <td className="ratio-col">{asNumber(player.winRate).toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="ranking-info">
        <h3>Comment progresser dans le classement ?</h3>
        <p>Votre rang est déterminé par votre ratio de victoires et le nombre total de parties jouées :</p>
        <ul>
          <li><strong>Novice :</strong> 0-20 parties jouées</li>
          <li><strong>Aventurier :</strong> 21+ parties jouées, ratio de victoires &gt; 40%</li>
          <li><strong>Champion :</strong> 50+ parties jouées, ratio de victoires &gt; 55%</li>
          <li><strong>Maître :</strong> 100+ parties jouées, ratio de victoires &gt; 65%</li>
          <li><strong>Légendaire :</strong> 200+ parties jouées, ratio de victoires &gt; 75%</li>
        </ul>
        <p>Les classements sont mis à jour en temps réel après chaque partie.</p>
      </div>
    </div>
  );
};

export default Rankings;
